import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import {debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { DropdownItem, SearchDropdownItems } from 'src/app/core/models/general.models';
import { UserPreferenceService } from 'src/app/core/services/user-preference.service';
import { addMask } from 'src/app/core/utils/functions';
import { AppState } from 'src/app/store';
import { setSearchDropdownItems, setSearchText } from 'src/app/store/search-bar/search-bar.actions';
import { getDropdownItems, getSearchText } from 'src/app/store/search-bar/search-bar.selectors';

@Component({
    selector: 'app-search-bar',
    templateUrl: './search-bar.component.html',
    styleUrls: ['./search-bar.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SearchBarComponent implements OnInit, OnDestroy {
  @Input() placeholder: string;
  SEARCH: string;
  searchSubscription: Subscription;
  caseMgmtRedirections: string[] = ['stroke-dashboard', 'analytics', 'improvement-areas',
    'my-hospital-info', 'quality-team-info', 'outpatient'];
  dropdownListSub: Subscription;
  dropdownItems: SearchDropdownItems; // Dropdown list for search results.
  filteredList: DropdownItem[] = [];
  pageKey: string;
  @ViewChild('searchDropdown') searchDropdown: NgbDropdown;
  @ViewChild('dropdownContent') dropdownContent: ElementRef;
  @ViewChild('searchInput', { static: false }) searchInput!: ElementRef;
  isInputFocused = false;
  
  constructor(
    private store: Store<AppState>, 
    public userPreferenceService: UserPreferenceService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.initSubscriptions();
  }

  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
    this.dropdownListSub?.unsubscribe();
  }

  /**
   * Initiate search by dispatching searchText to store.
   */
  search() {
    this.store.dispatch(setSearchText({ searchText: this.SEARCH }));
  }

  /**
   * Initialize subscriptions.
   */
  initSubscriptions() {
    this.searchSubscription = this.store.select(getSearchText)
      .pipe(debounceTime(300), distinctUntilChanged()).subscribe({
        next: data => {
          this.SEARCH = data;
          if (!this.SEARCH) this.filteredList = [];
          this.pageKey = location?.pathname?.split('/')?.slice(-1)?.[0];
          if(this.caseMgmtRedirections.some(page => this.pageKey === page) && this.SEARCH) {
            this.router.navigate(['/case-management'], {
              state: { redirectedViaSearch: true }
            });
          } else {
            this.dropdownItems?.showItems && this.onSearchInput();
            this.cdr.detectChanges(); 
          }
        }
      });
    
    this.dropdownListSub = this.store.select(getDropdownItems).subscribe({
      next: data => {
        this.dropdownItems = data;
      }
    });
  }

  /**
   * Handle search input and filter results.
   */
  onSearchInput() {
    this.filteredList = this.SEARCH ? this.dropdownItems?.items?.filter((result: DropdownItem) =>
      result?.label?.toLowerCase().includes(this.SEARCH.toLowerCase())
    ) : [];
    if (this.filteredList.length) {
      this.searchDropdown?.open();
      setTimeout(() => this.maskData());
    }
  }

  /**
   * Handle selection of a search result.
   * @param option The selected search option.
   */
  onSelectResult(option: DropdownItem) {
    this.searchDropdown?.close();
    const updatedItems = this.dropdownItems?.items?.map(item => ({
      ...item,
      selected: item?.id === option?.id
    }));
    this.store.dispatch(setSearchDropdownItems({ 
      dropdownItems: {
        showItems: true,
        items: updatedItems,
        dashboard: this.pageKey
      } 
    }));
  }

  /**
   * It is used to track the dropdown items.
   * @param index Index of the item.
   * @param item Item content.
   * @returns It returns the unique property of the item.
   */
  trackByFn(index, item) {
    return item?.id;
  }

  /**
   * It is called to mask the dropdown data when toggled.
   */
  maskData(): void {
    const elements = this.dropdownContent?.nativeElement.querySelectorAll('[data-mask="true"]');
    elements?.length && addMask(elements, `dropdown-${this.pageKey}`);
  }

  /**
   * Handle focus event on the input.
   */
  onFocus(): void {
    this.searchDropdown?.open();
    this.isInputFocused = true;
  }

  /**
   * Handle blur event on the input.
   */
  onBlur(): void {
    this.isInputFocused = false;
  }

  /**
   * Check if the search input is currently focused.
   * @returns True if the input is focused, false otherwise.
   */
  isSearchInputFocused(): boolean {
    return this.SEARCH?.length > 0 || this.isInputFocused;
  }

  /**
   * Reset the search input and dropdown.
   * If the input has text, clear it and keep the input focused.
   * If the input is empty, blur the input.
   */
  resetSearch(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.SEARCH) {
      // Clear the text and keep the input focused
      this.SEARCH = '';
      this.filteredList = [];
      this.searchInput?.nativeElement?.focus();
      this.search();
    } else {
      // Blur the input if there is no text
      this.searchInput?.nativeElement?.blur();
    }
  }
}
